import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Grid, Typography, useTheme, lighten } from "@mui/material";
import jsPDF from "jspdf";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertLineReportSTO } from "../../../../models/alert-line-report/alert-line-report-sto";
import { AlertLineURLApiService } from "../../../../services/alert-line/alert-line-url-api.service";
import { intlFormatDateShort } from "../../../../utils/time-utils";

interface Props {
  alertLineReport: AlertLineReportSTO;
}

const PostSubmitAlertLineReport = ({ alertLineReport }: Props) => {
  const tRP = "pages.alert_line_report.post_submit_alert_line_report";
  const { t } = useTranslation();
  const theme = useTheme();
  const [displayCopiedMessage, setDisplayCopiedMessage] = useState<boolean>(false);
  const [logo, setLogo] = useState<Blob | null>(null);

  const loadLogo = useCallback(async () => {
    if (alertLineReport.alertLineURL) {
      try {
        const response = await new AlertLineURLApiService().getAlertLineURLLogo(alertLineReport.alertLineURL?.id);
        if (response.size > 0) {
          setLogo(response);
        }
      } catch (error) {
        console.log("Error loading logo:", error);
      }
    }
  }, [alertLineReport.alertLineURL]);

  useEffect(() => {
    loadLogo();
  }, [alertLineReport]);

  const handleDownloadKey = useCallback(() => {
    const doc = new jsPDF();
    const maxWidth = doc.internal.pageSize.getWidth() - 30;

    const navigateToWidth = doc.getTextWidth(`${t(`${tRP}.downloaded_document.instructions.step_one`)} : `);
    const enterKeyWidth = doc.getTextWidth(`${t(`${tRP}.downloaded_document.instructions.enter_key`)} :`);

    if (logo) {
      const _logo = new Image();
      _logo.src = URL.createObjectURL(logo);
      _logo.width = 50;

      doc.addImage(_logo, "jpeg", doc.internal.pageSize.getWidth() - 35, 15, 20, 20);
    }

    doc.setFontSize(20);
    doc.setTextColor(theme.palette.text.primary);

    doc.text(`${t(`${tRP}.downloaded_document.keep_document`)}`, 15, 50, { maxWidth });

    doc.setFontSize(16);
    doc.text(`${t(`${tRP}.downloaded_document.instructions.title`)}`, 15, 75);

    doc.setFontSize(14);
    doc.text(`${t(`${tRP}.downloaded_document.instructions.step_one`)} : `, 15, 85);

    doc.setTextColor(theme.palette.primary.main);
    doc.textWithLink(`${alertLineReport.alertLineURL?.url}`, navigateToWidth + 10, 85, {
      url: alertLineReport.alertLineURL?.url,
    });

    doc.setTextColor(theme.palette.text.primary);
    doc.text(`${t(`${tRP}.downloaded_document.instructions.step_two`)}`, 15, 95);
    doc.text(`${t(`${tRP}.downloaded_document.instructions.enter_key`)} :`, 15, 105);
    doc.setFont("Inter", "bold");
    doc.text(alertLineReport.key, enterKeyWidth + 10, 105);

    doc.setTextColor(theme.palette.warning.main);
    doc.text(`${t(`${tRP}.downloaded_document.do_not_share`)}`, 15, 130);

    doc.save(`${intlFormatDateShort(new Date())}_${t(`${tRP}.downloaded_document.file_title`)}`);
  }, [alertLineReport, theme, t, logo]);

  const handleCopyKey = useCallback(() => {
    navigator.clipboard.writeText(alertLineReport.key);
    setDisplayCopiedMessage(true);
    setTimeout(() => {
      setDisplayCopiedMessage(false);
    }, 1000);
  }, [alertLineReport]);

  return (
    <Grid style={{ padding: "15px" }}>
      <Alert severity="success">{`${t(`${tRP}.intro_text`)}`}</Alert>
      <br />
      <Typography>{`${t(`${tRP}.key_message`)}`}</Typography>
      <Grid
        style={{
          display: "flex",
          border: `1px solid ${lighten(theme.palette.secondary.light, 0.5)}`,
          borderRadius: "8px",
          padding: "10px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography>
          <b>{alertLineReport.key}</b>
        </Typography>
        <Grid
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <Button
            onClick={() => handleCopyKey()}
            variant={displayCopiedMessage ? "outlined" : "contained"}
            style={{ textTransform: "none", width: "fit-content", transition: "all 0.5s ease" }}
          >
            {displayCopiedMessage ? (
              <Typography>
                {`${t(`${tRP}.copied`)} `} <FontAwesomeIcon icon={faCircleCheck} />
              </Typography>
            ) : (
              `${t(`${tRP}.copy`)}`
            )}
          </Button>
          <Button
            onClick={() => handleDownloadKey()}
            variant="contained"
            style={{ textTransform: "none", backgroundColor: theme.palette.success.main }}
          >{`${t(`${tRP}.download_key`)}`}</Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default PostSubmitAlertLineReport;
